import { createContext, useContext, useReducer, useMemo, useEffect, useCallback, useRef } from 'react';
import type {
    Project,
    Backsheet,
    BacksheetExpandRow,
    Quote,
    QuoteLineItem,
    Comment,
    ProjectUpdate,
} from '../types/dvms.types';
import * as api from '../api/api';
import { toCellString } from '../utils/coerce';

// ─── State ────────────────────────────────────────────────────────────────────

export type DvmsStatus = 'loading' | 'ready' | 'error';

/** Keys for lazily-loaded entity collections. */
export type DataKey = 'projects' | 'quotes' | 'updates';

export interface DvmsState {
    status: DvmsStatus;
    error: string | null;
    /** Transient error from a failed optimistic mutation — auto-dismissed after 5s. */
    mutationError: string | null;
    /** Transient success message — auto-dismissed after 3s. */
    mutationSuccess: string | null;
    /** Tracks which entity collections have been fetched from the API. */
    loaded: Partial<Record<DataKey, boolean>>;
    projects: Project[];
    backsheets: Backsheet[];
    quotes: Quote[];
    comments: Comment[];
    updates: ProjectUpdate[];
}

// ─── Actions ──────────────────────────────────────────────────────────────────

export type DvmsAction =
    | { type: 'SET_STATUS'; payload: DvmsStatus }
    | { type: 'SET_ERROR'; payload: string }
    | { type: 'LOAD_PROJECTS'; payload: Project[] }
    | { type: 'LOAD_BACKSHEETS'; payload: Backsheet[] }
    | { type: 'LOAD_QUOTES'; payload: Quote[] }
    | { type: 'LOAD_COMMENTS'; payload: Comment[] }
    | { type: 'LOAD_UPDATES'; payload: ProjectUpdate[] }
    | { type: 'ADD_PROJECT'; payload: Project }
    | { type: 'UPDATE_PROJECT'; payload: Partial<Project> & { id: string } }
    | { type: 'ADD_BACKSHEET'; payload: Backsheet }
    | { type: 'IMPORT_BACKSHEETS'; payload: Backsheet[] }
    | { type: 'UPDATE_BACKSHEET'; payload: Partial<Backsheet> & { id: string } }
    | { type: 'DELETE_BACKSHEET'; payload: { id: string } }
    | { type: 'APPROVE_ALL_BACKSHEETS'; payload: { projectId: string } }
    | { type: 'ADD_BACKSHEET_EXPAND_ROW'; payload: { backsheetId: string; row: BacksheetExpandRow } }
    | { type: 'REMOVE_BACKSHEET_EXPAND_ROW'; payload: { backsheetId: string; rowId: string } }
    | { type: 'ADD_QUOTE'; payload: Quote; pdfFile?: File | undefined }
    | { type: 'UPDATE_QUOTE'; payload: Partial<Quote> & { id: string } }
    | { type: 'UPDATE_QUOTE_LINE_ITEMS'; payload: { quoteId: string; lineItems: QuoteLineItem[] } }
    | { type: 'ADD_COMMENT'; payload: Comment }
    | { type: 'DELETE_COMMENT'; payload: { id: string } }
    | { type: 'SET_MUTATION_ERROR'; payload: string }
    | { type: 'CLEAR_MUTATION_ERROR' }
    | { type: 'SET_MUTATION_SUCCESS'; payload: string }
    | { type: 'CLEAR_MUTATION_SUCCESS' }
    | { type: 'RESTORE_SNAPSHOT'; payload: DvmsState };

// ─── Initial State ────────────────────────────────────────────────────────────

const INITIAL_STATE: DvmsState = {
    status: 'ready',
    error: null,
    mutationError: null,
    mutationSuccess: null,
    loaded: {},
    projects: [],
    backsheets: [],
    quotes: [],
    comments: [],
    updates: [],
};

// ─── Module-level author ref ──────────────────────────────────────────────────
// Updated by AuthProvider whenever the current user changes so audit trail
// entries include the author without threading it through every action.

let _currentAuthor = '';

/** Call this whenever the authenticated user changes. */
export function setCurrentAuthor(name: string): void {
    _currentAuthor = name;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function makeUpdate(
    projectId: string | undefined,
    kind: ProjectUpdate['kind'],
    summary: string,
    changes?: ProjectUpdate['changes'],
): ProjectUpdate {
    const base = {
        id: crypto.randomUUID(),
        kind,
        summary,
        changes,
        author: _currentAuthor,
        createdAt: new Date().toISOString(),
    };
    return projectId !== undefined ? { ...base, projectId } : base;
}

/** Compute field-level diff between old and new values (skipping id, projectId, type, expandRows). */
function computeChanges(old: Record<string, unknown>, patch: Record<string, unknown>): ProjectUpdate['changes'] {
    const skip = new Set(['id', 'projectId', 'type', 'expandRows']);
    const changes: NonNullable<ProjectUpdate['changes']> = [];
    for (const key of Object.keys(patch)) {
        if (skip.has(key)) {
            continue;
        }
        const oldVal = old[key];
        const newVal = patch[key];
        if (oldVal !== newVal && newVal !== undefined) {
            changes.push({ field: key, from: toCellString(oldVal), to: toCellString(newVal) });
        }
    }
    return changes.length > 0 ? changes : undefined;
}

// ─── Reducer ──────────────────────────────────────────────────────────────────

function reducer(state: DvmsState, action: DvmsAction): DvmsState {
    // Restore full state snapshot on optimistic rollback
    if (action.type === 'RESTORE_SNAPSHOT') {
        return { ...action.payload, mutationError: null, mutationSuccess: null };
    }

    // Generic LOAD_* handling for entity collections
    if (action.type.startsWith('LOAD_')) {
        const key = action.type.slice(5).toLowerCase() as DataKey;
        return { ...state, [key]: (action as { payload: unknown }).payload, loaded: { ...state.loaded, [key]: true } };
    }

    switch (action.type) {
        case 'SET_STATUS':
            return { ...state, status: action.payload };
        case 'SET_ERROR':
            return { ...state, status: 'error', error: action.payload };
        case 'SET_MUTATION_ERROR':
            return { ...state, mutationError: action.payload };
        case 'CLEAR_MUTATION_ERROR':
            return { ...state, mutationError: null };
        case 'SET_MUTATION_SUCCESS':
            return { ...state, mutationSuccess: action.payload };
        case 'CLEAR_MUTATION_SUCCESS':
            return { ...state, mutationSuccess: null };
        case 'ADD_PROJECT':
            return { ...state, projects: [...state.projects, action.payload] };
        case 'UPDATE_PROJECT': {
            const prev = state.projects.find((p) => p.id === action.payload.id);
            const newStatus = action.payload.status;
            const upd =
                newStatus !== undefined && prev !== undefined && prev.status !== newStatus
                    ? makeUpdate(
                          action.payload.id,
                          'status_changed',
                          `Status changed from ${prev.status} to ${newStatus}`,
                          [{ field: 'status', from: prev.status, to: newStatus }],
                      )
                    : null;
            return {
                ...state,
                projects: state.projects.map((p) => (p.id === action.payload.id ? { ...p, ...action.payload } : p)),
                updates: upd ? [...state.updates, upd] : state.updates,
            };
        }
        case 'ADD_BACKSHEET': {
            const bs = action.payload;
            const updatedProjects = state.projects.map((p) =>
                p.id === bs.projectId && p.status === 'created' ? { ...p, status: 'in_progress' as const } : p,
            );
            const upd = makeUpdate(bs.projectId, 'backsheet_added', `Added service: ${bs.language} — ${bs.studio}`);
            return {
                ...state,
                backsheets: [...state.backsheets, bs],
                projects: updatedProjects,
                updates: [...state.updates, upd],
            };
        }
        case 'IMPORT_BACKSHEETS': {
            const projectId = action.payload[0]?.projectId;
            const updatedProjects = projectId
                ? state.projects.map((p) =>
                      p.id === projectId && p.status === 'created' ? { ...p, status: 'in_progress' as const } : p,
                  )
                : state.projects;
            const upd = projectId
                ? makeUpdate(projectId, 'backsheet_imported', `Imported ${action.payload.length} backsheet(s) via CSV`)
                : null;
            return {
                ...state,
                backsheets: [...state.backsheets, ...action.payload],
                projects: updatedProjects,
                updates: upd ? [...state.updates, upd] : state.updates,
            };
        }
        case 'UPDATE_BACKSHEET': {
            const existing = state.backsheets.find((b) => b.id === action.payload.id);
            const bsChanges = existing
                ? computeChanges(existing as unknown as Record<string, unknown>, action.payload)
                : undefined;
            const upd = existing
                ? makeUpdate(
                      existing.projectId,
                      'backsheet_updated',
                      `Updated backsheet: ${existing.language}`,
                      bsChanges,
                  )
                : null;
            return {
                ...state,
                backsheets: state.backsheets.map((b) => (b.id === action.payload.id ? { ...b, ...action.payload } : b)),
                updates: upd ? [...state.updates, upd] : state.updates,
            };
        }
        case 'DELETE_BACKSHEET': {
            const delBs = state.backsheets.find((b) => b.id === action.payload.id);
            const upd = delBs
                ? makeUpdate(
                      delBs.projectId,
                      'backsheet_deleted',
                      `Deleted backsheet: ${delBs.language} — ${delBs.studio}`,
                  )
                : null;
            return {
                ...state,
                backsheets: state.backsheets.filter((b) => b.id !== action.payload.id),
                updates: upd ? [...state.updates, upd] : state.updates,
            };
        }
        case 'APPROVE_ALL_BACKSHEETS': {
            return {
                ...state,
                backsheets: state.backsheets.map((b) =>
                    b.projectId === action.payload.projectId ? { ...b, status: 'approved' as const } : b,
                ),
            };
        }
        case 'ADD_BACKSHEET_EXPAND_ROW': {
            const parentBs = state.backsheets.find((b) => b.id === action.payload.backsheetId);
            const upd = parentBs
                ? makeUpdate(
                      parentBs.projectId,
                      'backsheet_row_added',
                      `Added sub-service "${action.payload.row.subService}" to ${parentBs.language} backsheet`,
                  )
                : null;
            return {
                ...state,
                backsheets: state.backsheets.map((b) =>
                    b.id === action.payload.backsheetId
                        ? { ...b, expandRows: [...b.expandRows, action.payload.row] }
                        : b,
                ),
                updates: upd ? [...state.updates, upd] : state.updates,
            };
        }
        case 'REMOVE_BACKSHEET_EXPAND_ROW': {
            const parentBs2 = state.backsheets.find((b) => b.id === action.payload.backsheetId);
            const removedRow = parentBs2?.expandRows.find((r) => r.id === action.payload.rowId);
            const upd = parentBs2
                ? makeUpdate(
                      parentBs2.projectId,
                      'backsheet_row_removed',
                      `Removed sub-service "${removedRow?.subService ?? '?'}" from ${parentBs2.language} backsheet`,
                  )
                : null;
            return {
                ...state,
                backsheets: state.backsheets.map((b) =>
                    b.id === action.payload.backsheetId
                        ? { ...b, expandRows: b.expandRows.filter((r) => r.id !== action.payload.rowId) }
                        : b,
                ),
                updates: upd ? [...state.updates, upd] : state.updates,
            };
        }
        case 'ADD_QUOTE': {
            const q = action.payload;
            const updatedProjects = state.projects.map((p) =>
                p.id === q.projectId && p.status === 'in_progress' ? { ...p, status: 'quote_generated' as const } : p,
            );
            const upd = makeUpdate(q.projectId, 'quote_created', `Created quote: ${q.name}`);
            return {
                ...state,
                quotes: [...state.quotes, q],
                projects: updatedProjects,
                updates: [...state.updates, upd],
            };
        }
        case 'UPDATE_QUOTE':
            return {
                ...state,
                quotes: state.quotes.map((q) => (q.id === action.payload.id ? { ...q, ...action.payload } : q)),
            };
        case 'UPDATE_QUOTE_LINE_ITEMS':
            return {
                ...state,
                quotes: state.quotes.map((q) =>
                    q.id === action.payload.quoteId ? { ...q, lineItems: action.payload.lineItems } : q,
                ),
            };
        case 'ADD_COMMENT':
            return { ...state, comments: [...state.comments, action.payload] };
        case 'DELETE_COMMENT':
            return { ...state, comments: state.comments.filter((c) => c.id !== action.payload.id) };
        default:
            return state;
    }
}

// ─── API Side-effects ─────────────────────────────────────────────────────────
// Each mutation dispatches optimistically to the local store, then fires the
// corresponding API call in the background.

type SideEffectMap = {
    [K in DvmsAction['type']]?: (a: Extract<DvmsAction, { type: K }>) => Promise<unknown>;
};

const SIDE_EFFECTS: SideEffectMap = {
    // ADD_PROJECT only updates local state — CreateProjectPage calls api.createProject() directly.
    UPDATE_PROJECT: (a) => api.updateProject(a.payload.id, a.payload),
    ADD_BACKSHEET: (a) => api.createBacksheet(a.payload),
    IMPORT_BACKSHEETS: (a) => api.importBacksheets(a.payload),
    UPDATE_BACKSHEET: (a) => api.updateBacksheet(a.payload.id, a.payload),
    DELETE_BACKSHEET: (a) => api.deleteBacksheet(a.payload.id),
    APPROVE_ALL_BACKSHEETS: (a) => api.approveAllBacksheets(a.payload.projectId),
    ADD_BACKSHEET_EXPAND_ROW: (a) => api.addBacksheetExpandRow(a.payload.backsheetId, a.payload.row),
    REMOVE_BACKSHEET_EXPAND_ROW: (a) => api.removeBacksheetExpandRow(a.payload.backsheetId, a.payload.rowId),
    ADD_QUOTE: (a) => api.createQuote(a.payload),
    ADD_COMMENT: (a) => api.createComment(a.payload),
    DELETE_COMMENT: (a) => api.deleteComment(a.payload.id),
};

/** Maps action types to human-readable success messages. */
const SUCCESS_MESSAGES: Partial<Record<DvmsAction['type'], string>> = {
    ADD_PROJECT: 'Project created',
    UPDATE_PROJECT: 'Project updated',
    ADD_BACKSHEET: 'Service added',
    IMPORT_BACKSHEETS: 'Backsheets imported',
    UPDATE_BACKSHEET: 'Service updated',
    DELETE_BACKSHEET: 'Service deleted',
};

async function fireApiSideEffect(action: DvmsAction): Promise<string | undefined> {
    const fn = SIDE_EFFECTS[action.type] as ((a: DvmsAction) => Promise<unknown>) | undefined;
    if (!fn) {
        return undefined;
    }
    await fn(action);
    return SUCCESS_MESSAGES[action.type];
}

// ─── Lazy-load API map ────────────────────────────────────────────────────────

const apiLoaders: Record<DataKey, () => Promise<unknown>> = {
    projects: api.listProjects,
    quotes: api.listQuotes,
    updates: api.listUpdates,
};

function buildLoadAction(key: DataKey, data: unknown): DvmsAction {
    switch (key) {
        case 'projects':
            return { type: 'LOAD_PROJECTS', payload: data as Project[] };
        case 'quotes':
            return { type: 'LOAD_QUOTES', payload: data as Quote[] };
        case 'updates':
            return { type: 'LOAD_UPDATES', payload: data as ProjectUpdate[] };
    }
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface DvmsContextValue {
    state: DvmsState;
    dispatch: (action: DvmsAction) => Promise<void>;
    /** Trigger fetch for any entity keys not yet loaded. Safe to call repeatedly — deduplicates in-flight requests. */
    ensureLoaded: (keys: DataKey[]) => void;
}

const DvmsContext = createContext<DvmsContextValue | null>(null);

export function DvmsStoreProvider({ children }: { children: React.ReactNode }) {
    const [state, rawDispatch] = useReducer(reducer, INITIAL_STATE);

    // Wrap dispatch: optimistic local update + background API side-effect.
    // On success, show a transient success message. On failure, roll back to
    // the pre-mutation snapshot and show an error.
    // Returns a promise that resolves when the API call completes, so callers
    // (e.g. paginated admin tabs) can refetch server data after the mutation lands.
    const stateRef = useRef(state);
    stateRef.current = state;

    const dispatch = useCallback((action: DvmsAction): Promise<void> => {
        const snapshot = stateRef.current;
        rawDispatch(action);
        return fireApiSideEffect(action)
            .then((successMsg) => {
                if (successMsg) {
                    rawDispatch({ type: 'SET_MUTATION_SUCCESS', payload: successMsg });
                    setTimeout(() => {
                        rawDispatch({ type: 'CLEAR_MUTATION_SUCCESS' });
                    }, 3000);
                }
            })
            .catch((err: unknown) => {
                // Roll back to the state before the optimistic update
                rawDispatch({ type: 'RESTORE_SNAPSHOT', payload: snapshot });
                const message = err instanceof Error ? err.message : String(err);
                rawDispatch({ type: 'SET_MUTATION_ERROR', payload: message });
                setTimeout(() => {
                    rawDispatch({ type: 'CLEAR_MUTATION_ERROR' });
                }, 5000);
            });
    }, []);

    // Keep a ref to loaded so ensureLoaded can read current state without being recreated
    const loadedRef = useRef(state.loaded);
    loadedRef.current = state.loaded;

    // Track in-flight fetches to prevent duplicate requests
    const pendingLoads = useRef(new Set<DataKey>());

    const ensureLoaded = useCallback((keys: DataKey[]) => {
        for (const key of keys) {
            if (loadedRef.current[key] ?? pendingLoads.current.has(key)) {
                continue;
            }
            pendingLoads.current.add(key);
            void apiLoaders[key]()
                .then((data) => {
                    rawDispatch(buildLoadAction(key, data));
                })
                .catch((err: unknown) => {
                    rawDispatch({ type: 'SET_ERROR', payload: String(err) });
                })
                .finally(() => {
                    pendingLoads.current.delete(key);
                });
        }
    }, []); // stable — reads from refs only

    const value = useMemo(() => ({ state, dispatch, ensureLoaded }), [state, dispatch, ensureLoaded]);
    return <DvmsContext value={value}>{children}</DvmsContext>;
}

export function useDvms(): DvmsContextValue {
    const ctx = useContext(DvmsContext);
    if (!ctx) {
        throw new Error('useDvms must be used within DvmsStoreProvider');
    }
    return ctx;
}

/**
 * Triggers lazy-loading for the given entity keys on mount and returns whether
 * any of them are still pending. Renders the calling component with empty arrays
 * until loading completes — show a spinner while `true`.
 */
export function useEnsureLoaded(keys: DataKey[]): boolean {
    const { state, ensureLoaded } = useDvms();

    useEffect(() => {
        ensureLoaded(keys);
        // ensureLoaded is stable; keys is a literal array — running once on mount is correct
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ensureLoaded]);

    return keys.some((k) => !state.loaded[k]);
}
